import * as _ from 'lodash';
import * as DateFormatter from '../../../../Shared/DateFormatter';
import { DeleteButton } from '$Components/Shared/DeleteButton';
import { DeleteConfirmation } from '$Components/Shared/DeleteConfirmation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-solid-svg-icons';
import { FlagButton } from '../../../../Shared/FlagButton';
import { Grid, IconButton } from '../../../../../MaterialUIComponents';
import { IIntegrationPartnerDataInjectedProps, IntegrationPartnerDataService } from '$State/IntegrationPartnerDataFreezerService';
import { IVideoEventServiceInjectedProps, VideoEventService } from '$State/VideoEventFreezerService';
import { React, moment, unit } from '../../../../../Imports';
import { RolesEnum, canEditByRole } from '../../../../../externals/VerifyRole';
import { Section } from '../../DetailsSidePanel_SplitOff/Section';
import { VideoActions } from '../../DetailsSidePanel_SplitOff/VideoActions';
import { VideoEventDescription } from '../../../../Shared/VideoEventDescription';
import { VideoEventFlaggedByUsersCaption } from '../../../../Shared/VideoEventFlaggedByUsersCaption';
import { VideoEventResponse, VideoEventWorkflowStatusIdEnum, UserResponse } from '$Generated/api';
import { VideoRatingButton } from '../../../../Shared/VideoRatingButton';
import { getStatusLabel } from '../../../../../utilities/videoEventStatusUtility';
import { useEffect, useState } from 'react';
import * as scssStyles from '$CSS/settings.scss';

const styles = require('../VideoDetailsSection.scss') as {
    detailItem: string;
    detailItemName: string;
    detailItemValue: string;
    editDriverButton: string;
    flaggedByUsersCaptionContainer: string;
    sectionContent: string;
};

interface IVideoEventDetailsSectionBaseProps {
    canRate: boolean;
    changeStatusAndUpdateVideoEventHistory: (status: string) => void;
    currentDriverName: string;
    event: VideoEventResponse;
    eventStatus: string | undefined;
    handleDismissVideo: (eventId: number | undefined, action: string) => void;
    handleReopened: (eventId: number | undefined) => void;
    isDismissed: boolean;
    selectedRating: number | null;
    setCanComplete: (canComplete: boolean) => void;
    setCanRate: (canRate: boolean) => void;
    setIsDismissed: (isDismissed: boolean) => void;
    setSelectedRating: (rating: number | null) => void;
    setShowEditDriver: (showEditDriver: boolean) => void;
    showEditDriver: boolean;
    updateVideoEventHistory: () => void;
}

type IVideoEventDetailsSectionProps = IVideoEventDetailsSectionBaseProps &
    IVideoEventServiceInjectedProps &
    IIntegrationPartnerDataInjectedProps;

const getSpeedInfo = (event: VideoEventResponse) => {
    const isMetric = IntegrationPartnerDataService.getUserIsMetric();

    // Event Speed Info
    const hasStartSpeed = event.kphStart || event.kphStart === 0;
    const hasEndSpeed = event.kphEnd || event.kphEnd === 0;

    const startSpeed = hasStartSpeed ? unit.getSpeedValue(event.kphStart, isMetric) : null;
    const endSpeed = hasEndSpeed ? unit.getSpeedValue(event.kphEnd, isMetric) : null;
    const speedChange = hasStartSpeed && hasEndSpeed ? Math.abs(Math.round(endSpeed || 0) - Math.round(startSpeed || 0)) : null;

    const speedUnitString = unit.getSpeedUnitString(isMetric, false);

    // em dash unicode: \u2014
    const startSpeedFormatted = startSpeed !== null ? `${unit.formatSpeedString(startSpeed)} ${speedUnitString}` : '\u2014';
    const endSpeedFormatted = endSpeed !== null ? `${unit.formatSpeedString(endSpeed)} ${speedUnitString}` : '\u2014';
    const speedChangeFormatted = speedChange !== null ? `${unit.formatSpeedString(speedChange)} ${speedUnitString}` : '\u2014';

    // Posted Speed Limit Info
    const speedLimitEventId = 29;
    const criticalSpeedEventId = 34;
    ``;
    const json = event.eventJson;
    let postedSpeedFormatted = '';

    if (json && (event.videoEventType?.indexOf(speedLimitEventId) !== -1 || event.videoEventType?.indexOf(criticalSpeedEventId) !== -1)) {
        let eventJson = JSON.parse(json);

        try {
            // Speed Limit is currently stored only in the EventJson which is an arbitrary JSON that comes from the TelematicProvider
            let speedLimit = 0;
            //Attempt to pull speed limit from Streamax json
            const eventSource = eventJson.filter(
                (e: any) => e['TelematicRuleName'] == '56007' || e['TelematicRuleName'] == 'CRITICALSPEEDING',
            )[0]['Source'];

            speedLimit = JSON.parse(JSON.stringify(eventSource).toLocaleLowerCase())['alarmadditionalinfo']['speedlimit'];

            let isMetric = IntegrationPartnerDataService.getUserIsMetric();
            postedSpeedFormatted = `${isMetric ? Math.round(unit.mphToKph(speedLimit)) : speedLimit} ${unit.getSpeedUnitString(isMetric, false)}`;
        } catch {
            postedSpeedFormatted = '';
        }
    }

    return {
        startSpeed: startSpeedFormatted,
        endSpeed: endSpeedFormatted,
        speedChange: speedChangeFormatted,
        postedSpeed: postedSpeedFormatted,
    };
};

const _VideoEventDetailsSection: React.FC<IVideoEventDetailsSectionProps> = ({
    canRate,
    changeStatusAndUpdateVideoEventHistory,
    currentDriverName,
    event,
    eventStatus,
    handleDismissVideo,
    handleReopened,
    integrationPartnerData,
    isDismissed,
    selectedRating,
    setCanComplete,
    setCanRate,
    setIsDismissed,
    setSelectedRating,
    setShowEditDriver,
    showEditDriver,
    updateVideoEventHistory,
    videoEvents,
}) => {
    const [canEdit, setCanEdit] = useState(false);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [disabledDeleteButton, setDisabledDeleteButton] = useState(false);
    const [flaggedByUsers, setFlaggedByUsers] = useState(event.flaggedByUsers ?? ([] as UserResponse[]));

    const statusLabelId = (eventStatus !== undefined ? eventStatus : '') as VideoEventWorkflowStatusIdEnum;
    const statusLabel = getStatusLabel(statusLabelId);
    const eventDate = event.eventStartDate ? moment(event.eventStartDate) : moment();
    const element = React.useRef<HTMLDivElement>();
    const { currentUserResult } = integrationPartnerData.getState();
    const { startSpeed, endSpeed, speedChange, postedSpeed } = getSpeedInfo(event);

    const _canComplete = (event: VideoEventResponse): boolean => {
        return event.videoEventStatus !== 'Completed' && event.videoEventStatus !== 'Dismissed';
    };

    useEffect(() => {
        setCanEdit(canEditByRole([RolesEnum.videoEventEdit, RolesEnum.encompassVideo]));
        setCanComplete(_canComplete(event));
    }, []);

    const onFlagVideo = async (eventId: number | undefined): Promise<void> => {
        if (!canEdit) return;
        await videoEvents.flagVideoEvent(eventId);
        await updateVideoEventHistory();
        const currentUser = currentUserResult.data;

        if (eventIsFlaggedByCurrentUser()) {
            setFlaggedByUsers(
                flaggedByUsers.filter((user: UserResponse) => {
                    return user.integrationProviderSourceId !== currentUser.id;
                }),
            );
        } else {
            setFlaggedByUsers(
                flaggedByUsers.concat([
                    {
                        id: '',
                        integrationProviderSourceId: currentUser.id,
                        email: currentUser.email,
                        displayName: `${currentUser.firstName} ${currentUser.lastName}`,
                    },
                ]),
            );
        }
    };

    const eventIsFlaggedByCurrentUser = (): boolean => {
        return (
            _.find(flaggedByUsers, (user: UserResponse) => {
                return user.integrationProviderSourceId === currentUserResult.data.id;
            }) !== undefined
        );
    };

    const handleClearRating = async (eventId: number | undefined): Promise<void> => {
        await videoEvents.postVideoEventRatings({ videoEventIdList: [eventId ?? 0], ratingValue: 0 }); // rating value 0 clears the rating
        setCanComplete(true);
        setIsDismissed(false);
        setSelectedRating(null);
        setCanRate(true);
        changeStatusAndUpdateVideoEventHistory('In_Process');
    };

    const handleRateVideo = async (eventId: number | undefined, rating: number): Promise<void> => {
        await videoEvents.postVideoEventRatings({ videoEventIdList: [eventId ?? 0], ratingValue: rating });
        setCanComplete(true);
        updateVideoEventHistory();

        setSelectedRating(rating);
        setIsDismissed(false);
        setCanRate(true);
    };

    const toggleDeletePopup = () => {
        setShowDeletePopup(!showDeletePopup);
    };

    const changeToDisabled = (disabled: boolean) => {
        setDisabledDeleteButton(disabled);
    };

    const renderVideoEventDetailsButtons = () => {
        return (
            <VideoActions
                actions={[
                    <FlagButton
                        eventId={event.id}
                        flaggedByUsers={flaggedByUsers}
                        onFlagVideoEvent={onFlagVideo as (eventId: number | undefined) => void}
                    />,
                    <VideoRatingButton
                        event={event}
                        onDismissVideo={handleDismissVideo}
                        onClearRating={handleClearRating}
                        onRateVideo={handleRateVideo}
                        onReopenEvent={handleReopened}
                        selectedRating={selectedRating}
                        isDismissed={isDismissed}
                        canRate={canRate}
                        clearIsEnabled={event.videoEventStatus !== 'Completed' ? true : false}
                        anchor={element.current}
                        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                        transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    />,
                    <DeleteButton eventIds={event.id ?? 0} toggleDeletePopup={toggleDeletePopup} isDisabled={disabledDeleteButton} />,
                ]}
            />
        );
    };

    const toggleEditDriver = () => {
        if (!canEdit) return;
        setShowEditDriver(!showEditDriver);
    };

    return (
        <Section
            title="Event Details"
            actionButtons={renderVideoEventDetailsButtons()}
            content={
                <>
                    <div className={styles.flaggedByUsersCaptionContainer}>
                        <VideoEventFlaggedByUsersCaption tooltipPlacement="bottom" flaggedByUsers={flaggedByUsers} />
                    </div>

                    <div className={styles.sectionContent}>
                        <Grid container gap={1}>
                            <Grid item xs={12} sm={6}>
                                <div className={styles.detailItem}>
                                    <div className={styles.detailItemName}>Event Type</div>
                                    <div className={styles.detailItemValue}>
                                        <VideoEventDescription
                                            videoEventTypePairings={event.videoEventTypePairings}
                                            videoEventTypeString={event.videoEventTypeString}
                                            videoEventFontSize="14px"
                                            isVideoEventDetails={true}
                                        />
                                    </div>
                                </div>

                                <div className={styles.detailItem}>
                                    <div className={styles.detailItemName}>ID</div>
                                    <div className={styles.detailItemValue}>{event.displayId}</div>
                                </div>

                                <div className={styles.detailItem}>
                                    <div className={styles.detailItemName}>Date/Time</div>
                                    <div className={styles.detailItemValue}>
                                        {DateFormatter.date(eventDate)}
                                        <br />
                                        {DateFormatter.timeWithSecondsAndTimezone(eventDate)}
                                    </div>
                                </div>

                                <div className={styles.detailItem}>
                                    <div className={styles.detailItemName}>Event Status</div>
                                    <div className={styles.detailItemValue}>{statusLabel}</div>
                                </div>

                                <div className={styles.detailItem}>
                                    <div className={styles.detailItemName}>Driver</div>
                                    <div className={styles.detailItemValue}>
                                        <span>{currentDriverName.trim() !== '' ? currentDriverName : 'N/A'}</span>

                                        <IconButton
                                            className={styles.editDriverButton}
                                            aria-label="Edit driver name"
                                            title="Edit driver name"
                                            onClick={toggleEditDriver}
                                            size="small"
                                            disabled={!canEdit}
                                        >
                                            <FontAwesomeIcon icon={faEdit} size="xs" />
                                        </IconButton>
                                    </div>
                                </div>

                                <div className={styles.detailItem}>
                                    <div className={styles.detailItemName}>Vehicle</div>
                                    <div className={styles.detailItemValue}>{event.vehicleDescription || 'NA'}</div>
                                </div>

                                <div className={styles.detailItem}>
                                    <div className={styles.detailItemName}>Camera Serial Number</div>
                                    <div className={styles.detailItemValue}>{event.cameraDeviceSerialNumber || 'NA'}</div>
                                </div>
                            </Grid>

                            <Grid item xs={12} sm={5}>
                                <div className={styles.detailItem}>
                                    <div className={styles.detailItemName}>
                                        {scssStyles.styleEnvironment === 'encompass' ? 'Event Speed' : 'Start Speed'}
                                    </div>
                                    <div className={styles.detailItemValue}>{startSpeed}</div>
                                </div>
                                {scssStyles.styleEnvironment !== 'encompass' && (
                                    <>
                                        <div className={styles.detailItem}>
                                            <div className={styles.detailItemName}>End Speed</div>
                                            <div className={styles.detailItemValue}>{endSpeed}</div>
                                        </div>
                                        <div className={styles.detailItem}>
                                            <div className={styles.detailItemName}>Change Speed</div>
                                            <div className={styles.detailItemValue}>{speedChange}</div>
                                        </div>
                                    </>
                                )}

                                {postedSpeed && (
                                    <div className={styles.detailItem}>
                                        <div className={styles.detailItemName}>Posted Speed</div>
                                        <div className={styles.detailItemValue}>{postedSpeed}</div>
                                    </div>
                                )}
                            </Grid>
                        </Grid>
                    </div>
                    <DeleteConfirmation
                        eventIds={[event.id ?? 0]}
                        showDeletePopup={showDeletePopup}
                        toggleDeletePopup={toggleDeletePopup}
                        changeToDisabled={changeToDisabled}
                    />
                </>
            }
        />
    );
};

export const VideoEventDetailsSection = IntegrationPartnerDataService.inject(VideoEventService.inject(_VideoEventDetailsSection));
